import React, {useCallback, useMemo, useState, type AnimationEvent} from 'react';
import classNames from 'classnames';
import {FocusScope, type FocusScopeProps} from '@react-aria/focus';
import {mergeProps} from '@react-aria/utils';
import {
    DismissButton,
    OverlayContainer,
    useOverlay,
    useOverlayPosition,
    useOverlayTrigger,
} from '@react-aria/overlays';
import {type OverlayTriggerState, useOverlayTriggerState} from '@react-stately/overlays';
import type {AriaPositionProps} from '@react-aria/overlays';
import type {OverlayTriggerProps} from '@react-types/overlays';
import type {FocusableElement} from '@react-types/shared';
import type {AriaButtonProps} from '@react-aria/button';

import styles from './styles.module.css';

export const Popover = React.forwardRef<Element, any>(
    ({children, isOpen, onClose, style, shouldCloseOnInteractOutside, focusScopeProps, ...otherProps}, passedRef) => {
        const innerRef = React.createRef<Element>();
        const ref = (passedRef || innerRef) as React.RefObject<FocusableElement>;

        const {overlayProps, underlayProps} = useOverlay(
            {
                onClose,
                isOpen,
                isDismissable: true,
                shouldCloseOnBlur: true,
                shouldCloseOnInteractOutside,
            },
            ref,
        );

        return (
            <FocusScope restoreFocus={true} {...focusScopeProps}>
                <div
                    {...mergeProps(overlayProps, otherProps)}
                    ref={passedRef}
                    style={{...style, visibility: isOpen ? 'visible' : 'hidden'}}
                >
                    {React.cloneElement(children, underlayProps)}
                    <DismissButton onDismiss={onClose} />
                </div>
            </FocusScope>
        );
    },
);

type Props = {
    triggerNode?: Element | null;
    state?: OverlayTriggerState;
    zIndex?: number;
    focusScopeProps?: Omit<FocusScopeProps, 'children'>;
    renderTriggerElement?: (
        triggerProps: AriaButtonProps,
        ref: React.RefObject<HTMLButtonElement>,
        state: OverlayTriggerState
    ) => React.ReactNode;
    shouldCloseOnInteractOutside?: (element: Element) => boolean;
    onAnimationEnd?: (event: AnimationEvent) => void;
    animations?: {
        whenOpenClassName: string;
        whenCloseClassName: string;
    };
} & OverlayTriggerProps &
Omit<Partial<AriaPositionProps>, 'targetRef' | 'overlayRef' | 'isOpen'>;

export function PopoverOverlay(props: React.PropsWithChildren<Props>) {
    const {
        triggerNode,
        maxHeight,
        placement,
        containerPadding = 0,
        offset = 5,
        crossOffset,
        shouldFlip = true,
        renderTriggerElement,
        state: outsideState,
        shouldCloseOnInteractOutside,
        focusScopeProps,
        onAnimationEnd,
        animations = {
            whenOpenClassName: styles.fadeIn,
            whenCloseClassName: styles.fadeOut,
        },
        ...overlayTriggerProps
    } = props;
    const [waitForTransition, setWaitForTransition] = useState(false);
    const innerState = useOverlayTriggerState(overlayTriggerProps);
    const state = outsideState || innerState;
    const overlayRef = React.useRef<HTMLDivElement>(null);

    const triggerRef = React.useRef<HTMLButtonElement>(null);

    const ref = useMemo(() => {
        return triggerNode ? {
            current: triggerNode,
        } : triggerRef;
    }, [triggerNode]);

    const defaultShouldCloseOnInteractOutside = useCallback((element?: HTMLElement | null) => {
        // element передается из обработчика onPointerDown и в некоторых случаях может быть undefined
        // возможно , баг был исправлен в https://github.com/adobe/react-spectrum/issues/4121
        if (!element) {
            return false;
        }
        return !ref.current?.contains(element);
    }, [ref]);

    const {overlayProps, triggerProps} = useOverlayTrigger({type: 'menu'}, state, ref);

    const {overlayProps: positionProps} = useOverlayPosition({
        targetRef: ref,
        overlayRef,
        placement,
        containerPadding,
        offset,
        isOpen: state.isOpen,
        crossOffset,
        shouldFlip,
        maxHeight,
    });

    const handleAnimationEnd = (event: AnimationEvent) => {
        if (event.target !== overlayRef.current) {
            return;
        }

        onAnimationEnd?.(event);

        setWaitForTransition(state.isOpen);
    };

    return (
        <>
            {renderTriggerElement?.(triggerProps, triggerRef, state)}
            {(state.isOpen || waitForTransition) ? (
                <OverlayContainer>
                    <Popover
                        {...overlayProps}
                        {...positionProps}
                        className={classNames(styles.fadeAnimation, {
                            [animations.whenOpenClassName]: state.isOpen,
                            [animations.whenCloseClassName]: !state.isOpen,
                        })}
                        focusScopeProps={focusScopeProps}
                        ref={overlayRef}
                        isOpen={state.isOpen || waitForTransition}
                        style={{...positionProps.style, zIndex: props.zIndex ?? positionProps.style?.zIndex}}
                        onClose={state.close}
                        onAnimationEnd={handleAnimationEnd}
                        shouldCloseOnInteractOutside={shouldCloseOnInteractOutside ?? defaultShouldCloseOnInteractOutside}
                    >
                        {props.children}
                    </Popover>
                </OverlayContainer>
            ) : null}
        </>
    );
}

